import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { api } from "../../../services/apiService";
import { CheckoutHeader } from "../components/CheckoutHeader";
import { DateTimeField } from "../../../components/ops/DateTimeField";
import { SearchSelect } from "../../../components/ops/SearchSelect";
import { formatMoney, paymentError } from "../../../components/payments/paymentUtils";

const steps = ["Review plan", "Player details", "Waiver & payment"];
const fallbackWaiver = `Formula Soccer Center — Liability Waiver and Release

By checking the box below, you acknowledge that youth sports involve inherent risks of injury, and you voluntarily assume those risks on behalf of the enrolled player. You release Formula Soccer Center, its coaches, and staff from liability for injuries sustained during training sessions, except in cases of gross negligence.

You confirm that the player is physically able to participate in training activities, and you agree to notify staff of any relevant medical conditions.

This waiver remains in effect for the duration of the membership or program enrollment.`;

const emptyPlayer = { firstName: "", lastName: "", dateOfBirth: null, skillLevel: "developing", preferredFoot: "right" };
const recurringCopy = { monthly: "billed every month, cancel anytime", quarterly: "billed every 3 months, cancel anytime", annual: "billed every year, cancel anytime" };
const isRecurring = (plan) => recurringCopy[plan?.billingInterval] !== undefined;

export function CheckoutPage() {
  const { planSlug } = useParams();
  const { accessToken } = useAuth();

  const [step, setStep] = useState(0);
  const [plan, setPlan] = useState(null);
  const [players, setPlayers] = useState([]);
  const [playerId, setPlayerId] = useState("");
  const [creatingPlayer, setCreatingPlayer] = useState(false);
  const [newPlayer, setNewPlayer] = useState(emptyPlayer);
  const [waiverText, setWaiverText] = useState(fallbackWaiver);
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/memberships/plans/${planSlug}`),
      api.get("/players"),
      api.get("/cms/content/liability-waiver").catch(() => null),
    ])
      .then(([planResponse, playersResponse, contentResponse]) => {
        setPlan(planResponse.data.plan);
        const nextPlayers = playersResponse.data.players || [];
        setPlayers(nextPlayers);
        setPlayerId(nextPlayers[0]?._id || "");
        if (contentResponse?.data?.content?.body) setWaiverText(contentResponse.data.content.body);
      })
      .catch(() => setError("Unable to load this membership plan"))
      .finally(() => setLoading(false));
  }, [planSlug, accessToken]);

  const confirmPlayer = async () => {
    setError("");
    if (creatingPlayer) {
      if (!newPlayer.firstName || !newPlayer.lastName || !newPlayer.dateOfBirth) {
        setError("First name, last name, and date of birth are required");
        return;
      }
      setSubmitting(true);
      try {
        const response = await api.post("/players", { ...newPlayer, dateOfBirth: newPlayer.dateOfBirth.toISOString() });
        setPlayers([response.data.player, ...players]);
        setPlayerId(response.data.player._id);
        setCreatingPlayer(false);
        setStep(2);
      } catch (err) {
        setError(paymentError(err, "Unable to add player"));
      } finally {
        setSubmitting(false);
      }
      return;
    }
    if (!playerId) {
      setError("Select a player to continue");
      return;
    }
    setStep(2);
  };

  const submitCheckout = async () => {
    if (!waiverAccepted) {
      setError("Please accept the waiver to continue");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const checkoutResponse = await api.post("/checkout", { playerId, planId: plan._id, waiverAccepted: true });
      const sessionResponse = await api.post("/payments/session", { checkoutId: checkoutResponse.data.checkout._id });
      window.location.href = sessionResponse.data.alreadyPaid ? `/checkout/success?reference=${checkoutResponse.data.checkout.reference}` : sessionResponse.data.checkoutUrl;
    } catch (err) {
      setError(paymentError(err, "Unable to start checkout. Please try again."));
      setSubmitting(false);
    }
  };

  if (loading) return <main className="checkout-wrap"><CheckoutHeader />Loading…</main>;
  if (error && !plan) {
    return (
      <main className="checkout-wrap">
        <CheckoutHeader />
        <div className="ops-alert ops-alert--error">{error}</div>
        <Link to="/memberships" className="ops-card-link">Back to memberships</Link>
      </main>
    );
  }

  return (
    <main className="checkout-wrap">
      <CheckoutHeader />
      <div className="ops-steps" aria-label="Checkout progress">
        {steps.map((label, index) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div className={`ops-step ${index === step ? "is-active" : index < step ? "is-done" : ""}`} aria-current={index === step ? "step" : undefined}>
              <span className="ops-step-dot">{index < step ? <CheckCircle2 size={14} /> : index + 1}</span>
              {label}
            </div>
            {index < steps.length - 1 && <div className="ops-step-connector" />}
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="ops-card">
          <h1 className="ops-header-title" style={{ marginBottom: 6 }}>{plan.name}</h1>
          <p style={{ color: "var(--ops-muted)", fontSize: 14, marginBottom: 20 }}>{plan.description}</p>
          <div className="ops-stat-grid" style={{ gridTemplateColumns: "repeat(2, 1fr)" }}>
            <div className="ops-stat-card">
              <div className="ops-stat-card-content">
                <p className="ops-stat-card-label">Price</p>
                <p className="ops-stat-value">{formatMoney(plan.priceCents, plan.currency || "usd")}</p>
                <p className="ops-stat-hint">{recurringCopy[plan.billingInterval] || "one-time payment for this membership term"}</p>
              </div>
            </div>
            <div className="ops-stat-card">
              <div className="ops-stat-card-content">
                <p className="ops-stat-card-label">Sessions</p>
                <p className="ops-stat-value">{plan.sessionsPerMonth}</p>
                <p className="ops-stat-hint">per month</p>
              </div>
            </div>
          </div>
          {plan.features?.length > 0 && (
            <ul style={{ marginTop: 20, paddingLeft: 18, color: "var(--ops-muted)", fontSize: 14, lineHeight: 1.9 }}>
              {plan.features.map((feature) => <li key={feature}>{feature}</li>)}
            </ul>
          )}
          <div className="ops-form-actions">
            <button type="button" className="ops-button ops-button--primary" onClick={() => setStep(1)}>Continue</button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="ops-card">
          <h2 className="ops-card-title">Who is this membership for?</h2>

          {!creatingPlayer ? (
            <>
              {players.length > 0 ? (
                <div className="ops-form-group">
                  <label className="ops-form-label" htmlFor="checkout-player">Select player</label>
                  <SearchSelect inputId="checkout-player" options={players.map((player) => ({ value: player._id, label: `${player.firstName} ${player.lastName}` }))} value={playerId} onChange={setPlayerId} placeholder="Search players…" isClearable={false} />
                </div>
              ) : (
                <p style={{ color: "var(--ops-muted)", fontSize: 14, marginBottom: 16 }}>You don't have any players on your account yet.</p>
              )}
              <button type="button" className="ops-button ops-button--outline ops-button--small" onClick={() => setCreatingPlayer(true)}>
                + Add a new player
              </button>
            </>
          ) : (
            <div className="ops-form-grid">
              <div>
                <label className="ops-form-label">First name</label>
                <input required className="ops-input" value={newPlayer.firstName} onChange={(e) => setNewPlayer({ ...newPlayer, firstName: e.target.value })} />
              </div>
              <div>
                <label className="ops-form-label">Last name</label>
                <input required className="ops-input" value={newPlayer.lastName} onChange={(e) => setNewPlayer({ ...newPlayer, lastName: e.target.value })} />
              </div>
              <div>
                <label className="ops-form-label" htmlFor="checkout-new-player-dob">Date of birth</label>
                <DateTimeField id="checkout-new-player-dob" required showTime={false} maxDate={new Date()} value={newPlayer.dateOfBirth} onChange={(date) => setNewPlayer({ ...newPlayer, dateOfBirth: date })} placeholder="Select date of birth" />
              </div>
              <div>
                <label className="ops-form-label">Preferred foot</label>
                <select className="ops-select" value={newPlayer.preferredFoot} onChange={(e) => setNewPlayer({ ...newPlayer, preferredFoot: e.target.value })}>
                  <option value="right">Right</option>
                  <option value="left">Left</option>
                  <option value="both">Both</option>
                </select>
              </div>
              {players.length > 0 && (
                <button type="button" className="ops-button ops-button--secondary ops-button--small" onClick={() => setCreatingPlayer(false)} style={{ justifySelf: "start" }}>
                  Cancel
                </button>
              )}
            </div>
          )}

          {error && <div className="ops-alert ops-alert--error">{error}</div>}

          <div className="ops-form-actions">
            <button type="button" className="ops-button ops-button--secondary" onClick={() => setStep(0)}>Back</button>
            <button type="button" className="ops-button ops-button--primary" onClick={confirmPlayer} disabled={submitting}>
              {submitting ? "Saving…" : "Continue"}
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="ops-card">
          <h2 className="ops-card-title">Liability waiver</h2>
          <p className="checkout-payment-note">{isRecurring(plan) ? `You’ll be sent to Stripe for secure recurring billing — ${recurringCopy[plan.billingInterval]}. Enrollment activates only after the first payment is confirmed.` : "You’ll be sent to Stripe for a secure, one-time payment. Enrollment activates only after payment is confirmed."}</p>
          <div className="ops-waiver-box">{waiverText}</div>

          <label className="ops-checkbox-row">
            <input type="checkbox" checked={waiverAccepted} onChange={(e) => setWaiverAccepted(e.target.checked)} />
            I have read and accept the terms of this waiver on behalf of the enrolled player.
          </label>

          {error && <div className="ops-alert ops-alert--error" style={{ marginTop: 16 }}>{error}</div>}

          <div className="ops-form-actions">
            <button type="button" className="ops-button ops-button--secondary" onClick={() => setStep(1)}>Back</button>
            <button type="button" className="ops-button ops-button--primary" onClick={submitCheckout} disabled={submitting || !waiverAccepted}>
              {submitting ? "Redirecting to payment…" : "Proceed to payment"}
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
